import { getCompleteSizes, TAdFoxBannerSizeProps } from '@cian/adfox-component';
import { makeContextModule } from '@cian/react-utils';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectIsNewbuildingPremiumRate } from 'shared/selectors/newbuildingPremiumRate';
import { regionIdSelector } from 'shared/selectors/offerData/geo/regionIdSelector';
import { IPParams } from 'shared/types/adfox';

import { useAdfoxParams } from './internal/hooks/useAdfoxPuids';

import type { TAdfoxContextData } from './types';

const contextData = makeContextModule<TAdfoxContextData>({ contextName: 'AdfoxContextData' });

const {
  Context: { Provider },
} = contextData;

export const { useContext: useAdfoxContext, Context: AdfoxContext } = contextData;

export type TAdfoxContextProviderProps = Omit<TAdfoxContextData, 'params' | 'isHidden' | 'sizes' | 'regionId'> & {
  children: React.JSX.Element | React.JSX.Element[];
  pParams: IPParams;
  size: TAdFoxBannerSizeProps;
};

export const AdfoxContextProvider: FC<TAdfoxContextProviderProps> = props => {
  const { children, pParams, size, postViewAnalyticsData, bannerPlace } = props;

  const params = useAdfoxParams(pParams);

  const isPremiumNewbuilding = useSelector(selectIsNewbuildingPremiumRate);
  const regionId = useSelector(regionIdSelector);

  const contextValue = useMemo<TAdfoxContextData | null>(
    (): TAdfoxContextData | null =>
      regionId === undefined
        ? null
        : {
            params,
            sizes: getCompleteSizes(size),
            postViewAnalyticsData,
            bannerPlace,
            isHidden: isPremiumNewbuilding,
            regionId,
          },
    [bannerPlace, isPremiumNewbuilding, params, postViewAnalyticsData, regionId, size],
  );

  if (!contextValue) {
    return null;
  }

  return <Provider value={contextValue}>{children}</Provider>;
};
